import Swiper from "./swiper-bundle.esm.browser.min.js";

const MOBILE_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1200;
const TABLET_COLUMNS = 3;
const TABLET_SLIDES_PER_VIEW = 2.5;

class StrengthPrecisionSlider extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._resizeTimer = null;
    this._initialized = false;
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
    this._handleReadMoreChange = this._handleReadMoreChange.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    this.addEventListener("read-more:change", this._handleReadMoreChange);
    this._updateLayout();
    this._lastViewportMode = this._getViewportMode();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    this.removeEventListener("read-more:change", this._handleReadMoreChange);
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _handleReadMoreChange() {
    requestAnimationFrame(() => this._swiper?.update());
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._updateLayout();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    return {
      slideCount,
      sectionId: this.dataset.sectionId || "",
      desktopColumns: parseInt(this.dataset.desktopColumns || "4", 10),
      spaceBetween: parseInt(this.dataset.spaceBetween || "16", 10),
      mobileSlidesPerView: parseFloat(this.dataset.slidesPerViewMobile || "1.2"),
      showArrows: this.dataset.showArrows === "true",
      showPagination: this.dataset.showPagination === "true",
      enableLoop: this.dataset.enableLoop === "true",
      enableAutoplay: this.dataset.enableAutoplay === "true",
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed || "5000", 10),
      pauseOnHover: this.dataset.pauseOnHover !== "false",
      pauseOnInteraction: this.dataset.pauseOnInteraction === "true",
    };
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  _isTablet() {
    return (
      window.innerWidth >= MOBILE_BREAKPOINT &&
      window.innerWidth < DESKTOP_BREAKPOINT
    );
  }

  _getGridColumns(config) {
    if (this._isTablet()) {
      return Math.min(TABLET_COLUMNS, config.slideCount);
    }
    return config.desktopColumns;
  }

  _getSlidesPerView(config) {
    if (this._isMobile()) {
      return Math.min(config.mobileSlidesPerView, config.slideCount);
    }
    if (this._isTablet()) {
      return Math.min(TABLET_SLIDES_PER_VIEW, config.slideCount);
    }
    return Math.min(config.desktopColumns, config.slideCount);
  }

  _shouldUseSwiper(config) {
    if (config.slideCount <= 0) return false;
    if (this._isMobile()) return true;
    if (this._isTablet()) return config.slideCount > Math.floor(TABLET_SLIDES_PER_VIEW);
    return config.slideCount > config.desktopColumns;
  }

  _getViewportMode() {
    if (this._isMobile()) return "mobile";
    if (this._isTablet()) return "tablet";
    return "desktop";
  }

  _cleanupSwiperClasses() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slides = this.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "sps__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _applyGridMode(config) {
    this.classList.add("sps--grid");
    this.classList.remove("sps--swiper", "sps--tablet");
    this.style.setProperty(
      "--sps-grid-columns",
      String(this._getGridColumns(config)),
    );
    this._cleanupSwiperClasses();
  }

  _prepareSwiperStructure() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return null;

    swiperEl.classList.add("swiper", "sps__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { slideEls };
  }

  _syncSlideAccessibility(activeIndex) {
    const slides = this.querySelectorAll("[data-swiper-slide]");
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  _initSwiper(config) {
    const structure = this._prepareSwiperStructure();
    if (!structure) return;

    const { slideEls } = structure;
    const slidesPerViewForLoop = this._isTablet()
      ? TABLET_SLIDES_PER_VIEW
      : this._getSlidesPerView(config);
    const canNavigate = slideEls.length > 1;
    const canLoop =
      config.enableLoop &&
      canNavigate &&
      slideEls.length > slidesPerViewForLoop;

    this.classList.add("sps--swiper");
    this.classList.remove("sps--grid");
    this.classList.toggle("sps--tablet", this._isTablet());

    const slidesPerView = this._getSlidesPerView(config);

    const swiperConfig = {
      slidesPerView,
      spaceBetween: config.spaceBetween,
      watchOverflow: true,
      grabCursor: canNavigate,
      speed: 300,
      roundLengths: !this._isTablet(),
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
      },
    };

    if (canLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows && canNavigate) {
      const nextEl = this.querySelector(`.sps__nav-next--${config.sectionId}`);
      const prevEl = this.querySelector(`.sps__nav-prev--${config.sectionId}`);
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination && canNavigate) {
      const paginationEl = this.querySelector(
        `.sps__pagination--${config.sectionId}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.slideCount;
            return `<button type="button" class="${className}" aria-label="Go to slide ${num} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoplay && canNavigate) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        this._syncSlideAccessibility(swiper.realIndex ?? swiper.activeIndex);
      },
      resize: (swiper) => {
        existingOn.resize?.(swiper);
        swiper.update();
      },
    };

    const swiperEl = this.querySelector("[data-swiper]");
    this._swiper = new Swiper(swiperEl, swiperConfig);
    requestAnimationFrame(() => this._swiper?.update());
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._cleanupSwiperClasses();
  }

  _updateLayout() {
    const config = this._readConfig();
    const shouldUseSwiper = this._shouldUseSwiper(config);
    const isSwiperActive = Boolean(this._swiper);

    this._lastViewportMode = this._getViewportMode();

    if (!shouldUseSwiper) {
      this._destroySwiper();
      this._applyGridMode(config);
      return;
    }

    if (isSwiperActive) {
      this._destroySwiper();
    }

    this._initSwiper(config);
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const config = this._readConfig();
      const shouldUseSwiper = this._shouldUseSwiper(config);
      const isSwiperActive = Boolean(this._swiper);
      const viewportMode = this._getViewportMode();

      if (
        shouldUseSwiper !== isSwiperActive ||
        viewportMode !== this._lastViewportMode
      ) {
        this._lastViewportMode = viewportMode;
        this._updateLayout();
        return;
      }

      if (!shouldUseSwiper) {
        this._applyGridMode(config);
        return;
      }

      if (this._swiper && !this._swiper.destroyed) {
        this.classList.toggle("sps--tablet", this._isTablet());
        this._swiper.params.slidesPerView = this._getSlidesPerView(config);
        this._swiper.params.roundLengths = !this._isTablet();
        this._swiper.update();
      }
    }, 150);
  }
}

if (!customElements.get("strength-precision-slider")) {
  customElements.define("strength-precision-slider", StrengthPrecisionSlider);
}
